import isEqual from 'react-fast-compare';
import { getEventsWithNonZeroCounts } from 'src/contexts/CatalogDataContext/CatalogDataContext.utils';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { vars } from 'src/core/themes';
import { AggregateFunction } from 'src/modals/ColumnSettings/TableCustomColumnModal/TableCustomColumnModal.types';
import { CityIcon } from 'src/svgs/CityIcon';
import { CountryIcon } from 'src/svgs/CountryIcon';
import { DayIcon } from 'src/svgs/DayIcon';
import { EventIcon } from 'src/svgs/EventIcon';
import { MonthIcon } from 'src/svgs/MonthIcon';
import { PerformerIcon } from 'src/svgs/PerformerIcon';
import { SaleIcon } from 'src/svgs/SaleIcon';
import { VenueIcon } from 'src/svgs/VenueIcon';
import {
  PaperTicketsOutlineIcon,
  ResellAnytimeIcon,
  SeatIcon,
  SellerTraderOutlineIcon,
  SellOutlineIcon,
} from 'src/svgs/Viagogo';
import { WeekIcon } from 'src/svgs/WeekIcon';
import {
  ActionOutboxEntityType,
  EntityWithTicketsQuery,
  Event,
  EventWithData,
  ListingQuery,
  ListingReportMetricColumn,
  ListingReportMetrics,
  Performer,
  ReportGroupBy,
  ReportV2Client,
  SaleQuery,
  SaleReportMetrics,
  UIReportV2ListingQuery,
  UIReportV2SaleLineItemQuery,
  UIReportV2SaleQuery,
  UIReportV2SoldTicketQuery,
  UIReportV2TicketGroupQuery,
  Venue,
} from 'src/WebApiController';

import { ListingReportTableColumnId } from './columns/inventory/inventoryColumnUtils.types';
import { SalesReportTableValueColumnId } from './columns/sales/salesColumnUtils.types';
import { ContentId } from './constants/contentId';

export enum ReportTypes {
  Inventory = 'listing',
  Sale = 'sale',
}

export type ArTimeBucketWithData = Pick<
  SaleReportMetrics,
  'timeBkt' | 'ttlOutsBal'
>;

export enum ReportTypesV2 {
  InventoryV2 = 'listingV2',
  SaleV2 = 'saleV2',
  Commission = 'commission',
  Purchase = 'purchase',
  SaleLineItem = 'saleLineItem',
  PurchaseOrderLineItem = 'purchaseOrderLineItem',
  SoldTicket = 'soldTicket',
  CostOfGoodsSold = 'costOfGoodsSold',
  SaleCostAdjustments = 'saleCostAdjustments',
  TicketGroup = 'ticketGroup',
}

export type SaleReportMetricsWithGroupBy = SaleReportMetrics & {
  performer?: Performer;
  venue?: Venue;
  event?: Event;
  timeBucketsWithData?: ArTimeBucketWithData[];
};

export type ListingReportMetricsWithGroupBy = ListingReportMetrics & {
  performer?: Performer;
  venue?: Venue;
  event?: Event;
};

export const GetIconByGroupByType = (groupByType: ReportGroupBy) => {
  const { isDarkMode } = useSiteTheme();

  switch (groupByType) {
    case ReportGroupBy.Performer:
      return (
        <PerformerIcon
          fill={isDarkMode ? vars.color.textBrand : 'transparent'}
        />
      );
    case ReportGroupBy.Event:
    case ReportGroupBy.EventGenre:
      return (
        <EventIcon fill={isDarkMode ? vars.color.textBrand : 'transparent'} />
      );
    case ReportGroupBy.EventCity:
      return (
        <CityIcon fill={isDarkMode ? vars.color.textBrand : 'transparent'} />
      );
    case ReportGroupBy.EventCountry:
      return (
        <CountryIcon fill={isDarkMode ? vars.color.textBrand : 'transparent'} />
      );
    case ReportGroupBy.SeatingRow:
    case ReportGroupBy.SeatingSection:
      return <SeatIcon fill={vars.color.textBrand} />;
    case ReportGroupBy.SaleDay:
      return <DayIcon fill={vars.color.textBrand} />;
    case ReportGroupBy.SaleWeek:
      return <WeekIcon fill={vars.color.textBrand} />;
    case ReportGroupBy.SaleMonth:
      return <MonthIcon fill={vars.color.textBrand} />;
    case ReportGroupBy.SaleId:
      return <SaleIcon fill={vars.color.textBrand} />;
    case ReportGroupBy.ListingId:
      return <PaperTicketsOutlineIcon fill={vars.color.textBrand} />;
    case ReportGroupBy.PurchaseId:
      return <SellOutlineIcon fill={vars.color.textBrand} />;
    case ReportGroupBy.SaleMarketplace:
    case ReportGroupBy.Vendor:
      return <ResellAnytimeIcon fill={vars.color.textBrand} />;
    case ReportGroupBy.PurchasedBy:
    case ReportGroupBy.PricedBy:
    case ReportGroupBy.FulfilledBy:
      return <SellerTraderOutlineIcon fill={vars.color.textBrand} />;
    default:
      return (
        <VenueIcon fill={isDarkMode ? vars.color.textBrand : 'transparent'} />
      );
  }
};

export const listingMetricsToReportMetricRequest = (metric: string) => {
  if (metric === ListingReportTableColumnId.Zone.toString()) {
    return ListingReportMetricColumn.Zone;
  }

  // TODO complete this on https://thestubhub.atlassian.net/browse/POS-3290
  return undefined;
};

export const isReportFilterOverriden = (
  filterQuery: EntityWithTicketsQuery,
  filter: ListingQuery | SaleQuery | null | undefined
) => {
  const filterQueryToCompare = {
    ...filterQuery,
    eventIds: null,
    eventOrMappingIds: null,
    performerIds: null,
    venueIds: null,
    isSortDescending: null,
    sortBy: null,
    searchText: null,
    viewMode: null,
  };
  const reportConfigFilterToCompare = {
    ...filter,
    eventIds: null,
    eventOrMappingIds: null,
    performerIds: null,
    venueIds: null,
    isSortDescending: null,
    searchText: null,
    sortBy: null,
    viewMode: null,
  };

  return !isEqual(filterQueryToCompare, reportConfigFilterToCompare);
};

export const getEventsWithNonZeroCountsReporting = (
  data: EventWithData[] | null | undefined,
  requireEventNotNull: boolean | undefined,
  reportType:
    | ReportTypesV2.InventoryV2
    | ReportTypesV2.SaleV2
    | ReportTypesV2.SaleLineItem
    | ReportTypesV2.PurchaseOrderLineItem
    | ReportTypesV2.SoldTicket
    | ReportTypesV2.TicketGroup
) => {
  const entityType =
    reportType === ReportTypesV2.InventoryV2
      ? ActionOutboxEntityType.Listing
      : ActionOutboxEntityType.Sale;
  return getEventsWithNonZeroCounts(entityType, data, requireEventNotNull);
};

export const getEntityTypeByReportTypeForTag = (reportType: ReportTypesV2) => {
  switch (reportType) {
    case ReportTypesV2.InventoryV2:
      return ActionOutboxEntityType.Listing;
    case ReportTypesV2.SaleV2:
      return ActionOutboxEntityType.Sale;
    case ReportTypesV2.Purchase:
    case ReportTypesV2.TicketGroup:
      return ActionOutboxEntityType.Purchase;
  }
};

export function getCatalogCountForReport<TQuery>(
  client: ReportV2Client,
  filterQuery: TQuery,
  reportType:
    | ReportTypesV2.InventoryV2
    | ReportTypesV2.SaleV2
    | ReportTypesV2.SaleLineItem
    | ReportTypesV2.SoldTicket
    | ReportTypesV2.TicketGroup
) {
  switch (reportType) {
    case ReportTypesV2.InventoryV2:
      return client.getCatalogCountForInventoryReport(
        filterQuery as unknown as UIReportV2ListingQuery
      );
    case ReportTypesV2.SaleV2:
      return client.getCatalogCountForSalesReport(
        filterQuery as unknown as UIReportV2SaleQuery
      );
    case ReportTypesV2.SaleLineItem:
      return client.getCatalogCountForSaleLineItemReport(
        filterQuery as unknown as UIReportV2SaleLineItemQuery
      );
    case ReportTypesV2.SoldTicket:
      return client.getCatalogCountForSoldTicketReport(
        filterQuery as unknown as UIReportV2SoldTicketQuery
      );
    case ReportTypesV2.TicketGroup:
      return client.getCatalogCountForTicketGroupReport(
        filterQuery as unknown as UIReportV2TicketGroupQuery
      );
  }
}

export function reportValueColumnNameToExpression(
  columnName: string,
  aggregateFunction = AggregateFunction.SUM
) {
  // Margin is a special case that doesn't need an aggregate function
  if (
    columnName.toLocaleLowerCase() ===
    SalesReportTableValueColumnId.Margin.toLocaleLowerCase()
  ) {
    return columnName;
  }

  // Apply the specified aggregate function to the column
  return `${String(aggregateFunction)}(${columnName})`;
}

/**
 * Prefix for columnName and columnValue so that backend can identify
 *
 * Keep this the same with ReportingV2TableRecord.cs ReportingTagPrefix
 */
export const REPORTING_TAG_PREFIX = '__tag_';
export const LISTING_TAG_PREFIX = '__listingtag_';
export const ORIGINATED_FROM_TAG_PREFIX = '__originatedfromlistingtag_';
export const PURCHASE_ORDER_TAG_PREFIX = '__purchaseordertag_';

export function tagNameToReportingColumnName(name: string) {
  return `${REPORTING_TAG_PREFIX}${name}`;
}

export function tagNameToListingTagColumnName(name: string) {
  return `${LISTING_TAG_PREFIX}${name}`;
}

export function tagNameToOriginatedFromListingTagColumnName(name: string) {
  return `${ORIGINATED_FROM_TAG_PREFIX}${name}`;
}

export function tagNameToPurchaseOrderTagColumnName(name: string) {
  return `${PURCHASE_ORDER_TAG_PREFIX}${name}`;
}

export function getTagDisplayNameFromReportingColumnName(
  columnName: string
): string {
  if (columnName.startsWith(REPORTING_TAG_PREFIX)) {
    return columnName.substring(REPORTING_TAG_PREFIX.length);
  }
  if (columnName.startsWith(LISTING_TAG_PREFIX)) {
    return columnName.substring(LISTING_TAG_PREFIX.length);
  }
  if (columnName.startsWith(ORIGINATED_FROM_TAG_PREFIX)) {
    return columnName.substring(ORIGINATED_FROM_TAG_PREFIX.length);
  }
  if (columnName.startsWith(PURCHASE_ORDER_TAG_PREFIX)) {
    return columnName.substring(PURCHASE_ORDER_TAG_PREFIX.length);
  }
  return '';
}

export function isTagReportingColumnName(columnName: string): boolean {
  return (
    columnName.startsWith(REPORTING_TAG_PREFIX) ||
    columnName.startsWith(LISTING_TAG_PREFIX) ||
    columnName.startsWith(ORIGINATED_FROM_TAG_PREFIX) ||
    columnName.startsWith(PURCHASE_ORDER_TAG_PREFIX)
  );
}

export function tagReportingColumnNameToBadgeContentId(
  columnName: string
): ContentId {
  if (columnName.startsWith(REPORTING_TAG_PREFIX)) {
    return ContentId.Tag;
  }
  if (columnName.startsWith(LISTING_TAG_PREFIX)) {
    return ContentId.InventoryTag;
  }
  if (columnName.startsWith(ORIGINATED_FROM_TAG_PREFIX)) {
    return ContentId.InventoryTag;
  }
  if (columnName.startsWith(PURCHASE_ORDER_TAG_PREFIX)) {
    return ContentId.PurchaseTag;
  }
  return ContentId.Tag;
}
